import { useCreateShare, useRevokeShare, useShares } from '@numistic/data'
import { useTranslation } from '@numistic/i18n'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Field,
  Input,
} from '@numistic/ui'
import { useState } from 'react'

import { copyText } from '../lib/clipboard.js'
import { useErrorText } from '../lib/errors.js'
import { formatDate } from '../lib/format.js'

/** Ссылки «только чтение» на коллекцию: создание (токен показывается один раз) и отзыв. */
export function SharePanel({ collectionId, name }: { collectionId: string; name: string }) {
  const { t } = useTranslation()
  const errorText = useErrorText()
  const shares = useShares(collectionId)
  const create = useCreateShare()
  const revoke = useRevokeShare()
  const [includeChildren, setIncludeChildren] = useState(true)
  const [expires, setExpires] = useState('')
  const [copied, setCopied] = useState(false)
  const url = create.data?.url

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('share.title', { name })}</CardTitle>
        <p className="text-sm text-muted-foreground">{t('share.hint')}</p>
      </CardHeader>
      <CardContent className="grid gap-4">
        <form
          className="flex flex-wrap items-end gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            setCopied(false)
            create.mutate({
              collectionId,
              includeChildren,
              expiresAt: expires ? new Date(`${expires}T23:59:59`) : null,
            })
          }}
        >
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={includeChildren}
              onChange={(e) => setIncludeChildren(e.target.checked)}
            />
            {t('share.includeChildren')}
          </label>
          <Field label={t('share.expires')}>
            <Input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} />
          </Field>
          <Button type="submit" disabled={create.isPending}>
            {t('share.create')}
          </Button>
        </form>
        {url ? (
          <Alert className="grid gap-2">
            <span className="text-sm">{t('share.created')}</span>
            <div className="flex gap-2">
              <Input
                readOnly
                value={url}
                aria-label={t('share.link')}
                onFocus={(e) => e.target.select()}
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => void copyText(url).then(setCopied)}
              >
                {copied ? t('share.copied') : t('share.copy')}
              </Button>
            </div>
          </Alert>
        ) : null}
        {(create.error ?? revoke.error) ? (
          <Alert variant="destructive">{errorText.request(create.error ?? revoke.error)}</Alert>
        ) : null}
        {shares.data?.length ? (
          <ul className="grid gap-2 text-sm" aria-label={t('share.existing')}>
            {shares.data.map((s) => {
              const expired = s.expiresAt !== null && s.expiresAt < new Date()
              return (
                <li key={s.id} className="flex flex-wrap items-center gap-2 border-t pt-2">
                  <span>{t('share.createdAt', { date: formatDate(s.createdAt) })}</span>
                  <Badge variant="muted">
                    {s.includeChildren ? t('share.withChildren') : t('share.rootOnly')}
                  </Badge>
                  {s.expiresAt ? (
                    <span className="text-muted-foreground">
                      {t('share.until', { date: formatDate(s.expiresAt) })}
                    </span>
                  ) : null}
                  {s.revokedAt ? (
                    <Badge variant="destructive">{t('share.revoked')}</Badge>
                  ) : expired ? (
                    <Badge variant="destructive">{t('share.expired')}</Badge>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="ml-auto"
                      onClick={() => revoke.mutate(s.id)}
                    >
                      {t('share.revoke')}
                    </Button>
                  )}
                </li>
              )
            })}
          </ul>
        ) : null}
      </CardContent>
    </Card>
  )
}
